/**
 * Exact fixed-scale amounts for the Bybit spot books: every quantity, price, fee and USDT amount
 * is a `bigint` count of 10⁻¹⁸ units.
 *
 * Why 18 decimals is enough: Bybit spot instruments publish `basePrecision`, `quotePrecision`,
 * `tickSize`, `minOrderQty` and fees with at most 10–12 fraction digits (the finest is a sub-cent
 * memecoin tick around 1e-10), so 18 decimals represents every exchange value exactly with
 * headroom. A value with more digits is rejected at parse time, never silently truncated. Products
 * of two amounts are kept at 36 decimals until a single explicit floor, so planning never stacks
 * two roundings. `bigint` has no upper bound, so large balances and sums cannot overflow.
 */
export const SCALE = 18;
export const ONE = 10n ** BigInt(SCALE);
const UNSIGNED = /^(0|[1-9]\d{0,59})(\.\d{1,18})?$/;
const SIGNED = /^-?(0|[1-9]\d{0,59})(\.\d{1,18})?$/;
/**
 * Strict decimal string → units. Rejects empty strings, whitespace, exponents, a leading `+`,
 * redundant leading zeros, a bare or trailing `.`, more than 18 fraction digits, and a sign unless
 * `allowNegative` is set (`-0` is rejected even then).
 */
export function parseAmount(value, options = {}) {
    const pattern = options.allowNegative === true ? SIGNED : UNSIGNED;
    if (!pattern.test(value) || /^-0(\.0+)?$/.test(value))
        throw new Error("Invalid spot amount");
    const negative = value.startsWith("-");
    const body = negative ? value.slice(1) : value;
    const [whole = "0", fraction = ""] = body.split(".");
    const units = BigInt(whole) * ONE + BigInt(fraction.padEnd(SCALE, "0"));
    return negative ? -units : units;
}
/** A price or step: strictly positive. */
export function parsePositive(value) {
    const units = parseAmount(value);
    if (units <= 0n)
        throw new Error("Spot amount must be positive");
    return units;
}
/** Canonical decimal string: no trailing fraction zeros, no trailing `.`, `-` only below zero. */
export function formatAmount(units) {
    const negative = units < 0n;
    const abs = negative ? -units : units;
    const whole = (abs / ONE).toString();
    const fraction = (abs % ONE).toString().padStart(SCALE, "0").replace(/0+$/, "");
    return `${negative ? "-" : ""}${whole}${fraction === "" ? "" : `.${fraction}`}`;
}
/** Floor division (toward −∞) with a strictly positive divisor. */
export function floorDiv(numerator, denominator) {
    if (denominator <= 0n)
        throw new Error("Division by a non-positive amount");
    const q = numerator / denominator;
    return numerator % denominator !== 0n && numerator < 0n ? q - 1n : q;
}
/** a × b at 18 decimals, floored. */
export function mulFloor(a, b) {
    return floorDiv(a * b, ONE);
}
/** a ÷ b at 18 decimals, floored; b must be positive. */
export function divFloor(a, b) {
    return floorDiv(a * ONE, b);
}
/** a × num ÷ den in one exact step, floored (no intermediate rounding). */
export function mulDivFloor(a, num, den) {
    return floorDiv(a * num, den);
}
/** Largest multiple of `step` not above `value` (`value ≥ 0`, `step > 0`). */
export function floorToStep(value, step) {
    if (value < 0n)
        throw new Error("Cannot floor a negative amount to a step");
    return value - (value % positive(step));
}
export const min = (a, b) => (a < b ? a : b);
export const max = (a, b) => (a > b ? a : b);
export const sum = (values) => {
    let total = 0n;
    for (const v of values)
        total += v;
    return total;
};
function positive(value) {
    if (value <= 0n)
        throw new Error("Spot amount must be positive");
    return value;
}
/**
 * Splits `total` over `weights` exactly: share_i = ⌊total·w_i/Σw⌋, and the remainder goes to the
 * largest weight (ties → lowest index). With `capped`, no share may exceed its own weight
 * (`total ≤ Σw` required): remainder the largest cannot absorb overflows to the next largest, so a
 * fill is never allocated beyond what a contributor asked for. Callers order entries
 * deterministically, which makes the whole split deterministic.
 */
export function splitProRata(total, weights, options = {}) {
    if (total < 0n || weights.some((w) => w < 0n))
        throw new Error("Negative pro-rata input");
    const all = sum(weights);
    if (all === 0n) {
        if (total === 0n)
            return weights.map(() => 0n);
        throw new Error("Pro-rata split over zero weights");
    }
    const capped = options.capped === true;
    if (capped && total > all)
        throw new Error("Pro-rata total exceeds capped weights");
    const shares = weights.map((w) => (total * w) / all);
    let remainder = total - sum(shares);
    const order = weights
        .map((w, i) => ({ w, i }))
        .sort((a, b) => (a.w === b.w ? a.i - b.i : a.w > b.w ? -1 : 1));
    for (const { w, i } of order) {
        if (remainder === 0n)
            break;
        const share = shares[i] ?? 0n;
        const room = capped ? w - share : remainder;
        const add = min(room, remainder);
        shares[i] = share + add;
        remainder -= add;
    }
    if (remainder !== 0n)
        throw new Error("Pro-rata remainder left over");
    return shares;
}
